import React, { useState, useEffect } from 'react';
import { seoService } from '../services/seoService';
import { pageService } from '../services/pageService';
import { blogService } from '../services/blogService';
import { SEO_TOOLTIPS, PageSEOAnalysis, SEOIssue, TrackedKeyword, SuggestedKeyword } from '../types/seo';

type TabType = 'overview' | 'analyzer' | 'keywords' | 'content' | 'technical';

// Tooltip Component
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex items-center">
      {children}
      <button onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} onClick={() => setShow(!show)}
        className="ml-1 w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-xs flex items-center justify-center hover:bg-blue-100 hover:text-blue-600 transition-colors cursor-help">
        ?
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

// Score Circle Component
const ScoreCircle: React.FC<{ score: number; size?: 'sm' | 'md' | 'lg'; label?: string }> = ({ score, size = 'md', label }) => {
  const sizes = { sm: 'w-16 h-16 text-lg', md: 'w-24 h-24 text-2xl', lg: 'w-32 h-32 text-4xl' };
  const getColor = (s: number) => s >= 80 ? 'text-green-500' : s >= 60 ? 'text-yellow-500' : s >= 40 ? 'text-orange-500' : 'text-red-500';
  const getBgColor = (s: number) => s >= 80 ? 'stroke-green-500' : s >= 60 ? 'stroke-yellow-500' : s >= 40 ? 'stroke-orange-500' : 'stroke-red-500';
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${sizes[size]} flex items-center justify-center`}>
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
          <circle cx="50" cy="50" r="45" fill="none" className={getBgColor(score)} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
        </svg>
        <span className={`font-bold ${getColor(score)}`}>{score}</span>
      </div>
      {label && <span className="text-xs text-gray-500 mt-1">{label}</span>}
    </div>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ status: 'pass' | 'warning' | 'fail' | 'good' | 'needs-work' | 'missing' }> = ({ status }) => {
  const styles = {
    pass: 'bg-green-100 text-green-700', good: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700', 'needs-work': 'bg-yellow-100 text-yellow-700',
    fail: 'bg-red-100 text-red-700', missing: 'bg-red-100 text-red-700'
  };
  const icons = { pass: '✓', good: '✓', warning: '!', 'needs-work': '!', fail: '✗', missing: '✗' };
  const labels = { pass: 'Pass', good: 'Good', warning: 'Warning', 'needs-work': 'Needs Work', fail: 'Fail', missing: 'Missing' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {icons[status]} {labels[status]}
    </span>
  );
};

// Icons
const SearchIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const ChartIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>;
const KeyIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>;
const DocumentIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const CogIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const PlusIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
const TrashIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const RefreshIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;

const SEODashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [pages, setPages] = useState<any[]>([]);
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [pageAnalyses, setPageAnalyses] = useState<PageSEOAnalysis[]>([]);
  const [issues, setIssues] = useState<SEOIssue[]>([]);
  const [trackedKeywords, setTrackedKeywords] = useState<TrackedKeyword[]>([]);
  const [suggestedKeywords] = useState<SuggestedKeyword[]>(seoService.getSuggestedKeywords());
  const [selectedPageForAnalysis, setSelectedPageForAnalysis] = useState<string>('');
  const [overallScore, setOverallScore] = useState({ overall: 0, metaTags: 0, content: 0, technical: 0, keywords: 0 });
  
  // Keyword modal state
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [newKeyword, setNewKeyword] = useState({ keyword: '', pageId: '', isPrimary: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch pages and blog posts
      let pagesData: any[] = [];
      let blogPostsData: any[] = [];
      
      try {
        pagesData = await pageService.getAllPages(true) || [];
        // Normalize page data - map pageSlug/pageTitle to slug/title for consistency
        pagesData = pagesData.map(p => ({
          ...p,
          slug: p.pageSlug || p.slug,
          title: p.pageTitle || p.title
        }));
        console.log('SEO Dashboard - Pages loaded:', pagesData.length, pagesData);
      } catch (e) {
        console.warn('Could not fetch pages:', e);
      }
      
      try {
        const blogResponse = await blogService.getBlogPosts();
        console.log('SEO Dashboard - Blog response:', blogResponse);
        // Handle nested response structure: { success, data: { success, data: { posts } } } or { success, data: { posts } }
        if (blogResponse.success && blogResponse.data) {
          // Check if data has nested structure
          const responseData = blogResponse.data as any;
          if (responseData.data && responseData.data.posts) {
            // Nested: { success, data: { success, data: { posts } } }
            blogPostsData = responseData.data.posts;
          } else if (responseData.posts) {
            // Direct: { success, data: { posts } }
            blogPostsData = responseData.posts;
          } else if (Array.isArray(responseData)) {
            // Array directly
            blogPostsData = responseData;
          }
        }
        console.log('SEO Dashboard - Blog posts loaded:', blogPostsData.length, blogPostsData);
      } catch (e) {
        console.warn('Could not fetch blog posts:', e);
      }
      
      setPages(pagesData);
      setBlogPosts(blogPostsData);
      setTrackedKeywords(seoService.getTrackedKeywords());
      
      // Auto-scan if we have data
      if (pagesData.length > 0 || blogPostsData.length > 0) {
        await runScan(pagesData, blogPostsData);
      }
    } catch (error) {
      console.error('Error loading SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runScan = async (pagesData: any[], blogData: any[]) => {
    setScanning(true);
    const analyses: PageSEOAnalysis[] = [];
    const allIssues: SEOIssue[] = [];
    
    // Analyze CMS pages
    for (const page of pagesData) {
      const slug = page.pageSlug || page.slug || '';
      const title = page.pageTitle || page.title || 'Untitled';
      // Combine all text from sections: title + description + content + any items
      const content = page.sections?.map((s: any) => {
        const parts = [s.title || '', s.description || '', s.content || ''];
        // Also include items if they exist (for lists, features, etc.)
        if (s.items && Array.isArray(s.items)) {
          s.items.forEach((item: any) => {
            parts.push(item.title || '', item.description || '', item.text || '');
          });
        }
        return parts.join(' ');
      }).join(' ') || '';
      const analysis = await seoService.analyzePage({
        url: `/${slug}`,
        title: title,
        metaTitle: page.seo?.metaTitle || title,
        metaDescription: page.seo?.metaDescription || '',
        content,
        h1: title,
        h2s: page.sections?.filter((s: any) => s.title).map((s: any) => s.title) || [],
        images: []
      });
      analyses.push(analysis);
      allIssues.push(...seoService.generateIssues(analysis));
    }
    
    // Analyze blog posts
    for (const post of blogData) {
      const analysis = await seoService.analyzePage({
        url: `/blog/${post.slug}`,
        title: post.title,
        metaTitle: post.seo?.metaTitle || post.title,
        metaDescription: post.seo?.metaDescription || post.excerpt || '',
        content: post.content || '',
        h1: post.title,
        h2s: [],
        images: []
      });
      analyses.push(analysis);
      allIssues.push(...seoService.generateIssues(analysis));
    }
    
    setPageAnalyses(analyses);
    setIssues(allIssues);
    const score = seoService.calculateOverallScore(analyses);
    setOverallScore(score);
    seoService.addScoreToHistory(score.overall);
    setScanning(false);
  };

  const handleAddKeyword = () => {
    if (!newKeyword.keyword || !newKeyword.pageId) return;
    const page = [...pages, ...blogPosts].find(p => p._id === newKeyword.pageId);
    if (!page) return;
    
    const added = seoService.addKeyword(
      newKeyword.keyword,
      newKeyword.pageId,
      page.slug ? `/${page.slug}` : `/blog/${page.slug}`,
      page.title,
      newKeyword.isPrimary
    );
    setTrackedKeywords([...trackedKeywords, added]);
    setNewKeyword({ keyword: '', pageId: '', isPrimary: false });
    setShowKeywordModal(false);
  };

  const handleRemoveKeyword = (id: string) => {
    seoService.removeKeyword(id);
    setTrackedKeywords(trackedKeywords.filter(k => k.id !== id));
  };

  const allPages = [...pages.map(p => ({ ...p, type: 'page' })), ...blogPosts.map(p => ({ ...p, type: 'blog' }))];
  const issuesByCategory = issues.reduce((acc, issue) => {
    acc[issue.category] = (acc[issue.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartIcon },
    { id: 'analyzer', label: 'Page Analyzer', icon: SearchIcon },
    { id: 'keywords', label: 'Keywords', icon: KeyIcon },
    { id: 'content', label: 'Content', icon: DocumentIcon },
    { id: 'technical', label: 'Technical', icon: CogIcon },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-green-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-500 font-medium">Loading SEO Dashboard...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no pages or blog posts
  const hasNoData = pages.length === 0 && blogPosts.length === 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 via-teal-600 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">SEO Dashboard</h1>
              <p className="text-green-100 text-sm">Optimize your website for better search rankings</p>
            </div>
          </div>
          <button onClick={() => runScan(pages, blogPosts)} disabled={scanning || hasNoData}
            className="px-5 py-2.5 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 self-start lg:self-auto disabled:opacity-50">
            <RefreshIcon /> {scanning ? 'Scanning...' : 'Scan All Pages'}
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <Tooltip text={SEO_TOOLTIPS.seoScore}>
              <span className="text-green-100 text-sm">SEO Score</span>
            </Tooltip>
            <div className="text-3xl font-bold mt-1">{overallScore.overall}/100</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <Tooltip text="Total number of SEO issues found across all pages. Fix these to improve your score.">
              <span className="text-green-100 text-sm">Issues Found</span>
            </Tooltip>
            <div className="text-3xl font-bold mt-1">{issues.length}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <Tooltip text="Number of pages and blog posts analyzed for SEO.">
              <span className="text-green-100 text-sm">Pages Scanned</span>
            </Tooltip>
            <div className="text-3xl font-bold mt-1">{pageAnalyses.length}</div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <Tooltip text="Keywords you're actively tracking and optimizing for.">
              <span className="text-green-100 text-sm">Keywords Tracked</span>
            </Tooltip>
            <div className="text-3xl font-bold mt-1">{trackedKeywords.length}</div>
          </div>
        </div>
      </div>

      {/* No Data Warning */}
      {hasNoData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-3">📄</div>
          <h3 className="font-semibold text-gray-900 mb-2">No Pages Found</h3>
          <p className="text-gray-600 mb-4">
            The SEO Dashboard needs pages or blog posts to analyze. Make sure your API is running and you have content in your CMS.
          </p>
          <div className="text-sm text-gray-500">
            <p>Found: {pages.length} pages, {blogPosts.length} blog posts</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              <tab.icon /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Score Breakdown
              <Tooltip text="Your SEO score broken down by category. Focus on improving the lowest scores first.">
                <span></span>
              </Tooltip>
            </h3>
            <div className="flex justify-center mb-6">
              <ScoreCircle score={overallScore.overall} size="lg" label="Overall" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <ScoreCircle score={overallScore.metaTags} size="sm" />
                <Tooltip text={SEO_TOOLTIPS.metaTitle}><span className="text-xs text-gray-600 mt-1 block">Meta Tags</span></Tooltip>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <ScoreCircle score={overallScore.content} size="sm" />
                <Tooltip text={SEO_TOOLTIPS.wordCount}><span className="text-xs text-gray-600 mt-1 block">Content</span></Tooltip>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <ScoreCircle score={overallScore.technical} size="sm" />
                <Tooltip text={SEO_TOOLTIPS.sitemap}><span className="text-xs text-gray-600 mt-1 block">Technical</span></Tooltip>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <ScoreCircle score={overallScore.keywords} size="sm" />
                <Tooltip text={SEO_TOOLTIPS.keywordDensity}><span className="text-xs text-gray-600 mt-1 block">Keywords</span></Tooltip>
              </div>
            </div>
          </div>

          {/* Issues by Category */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Issues by Category
              <Tooltip text="SEO issues grouped by type. Click on a category to see detailed issues.">
                <span></span>
              </Tooltip>
            </h3>
            <div className="space-y-3">
              {Object.entries(issuesByCategory).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => setActiveTab('analyzer')}>
                  <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${count > 3 ? 'bg-red-500' : count > 1 ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                    <span className="capitalize text-gray-700">{category}</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${count > 3 ? 'bg-red-100 text-red-700' : count > 1 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                    {count} issue{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
              {Object.keys(issuesByCategory).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🎉</div>
                  <p>No issues found! Great job!</p>
                </div>
              )}
            </div>
          </div>

          {/* Priority Fixes */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Priority Fixes
              <Tooltip text="The most important SEO issues to fix first. High impact issues should be addressed immediately.">
                <span></span>
              </Tooltip>
            </h3>
            <div className="space-y-3">
              {issues.filter(i => i.impact === 'high').slice(0, 5).map((issue, idx) => (
                <div key={issue.id} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">{idx + 1}.</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{issue.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{issue.page}</p>
                      <Tooltip text={issue.howToFix}>
                        <span className="text-xs text-blue-600 mt-1 cursor-help">How to fix →</span>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ))}
              {issues.filter(i => i.impact === 'high').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">✅</div>
                  <p>No high-priority issues!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Page Analyzer Tab */}
      {activeTab === 'analyzer' && (
        <div className="space-y-6">
          {/* Page Selector */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <SearchIcon /> Select Page to Analyze
              <Tooltip text="Choose a page or blog post to see detailed SEO analysis and recommendations.">
                <span></span>
              </Tooltip>
            </h3>
            <select value={selectedPageForAnalysis} onChange={(e) => setSelectedPageForAnalysis(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 bg-white">
              <option value="">Select a page...</option>
              <optgroup label="Website Pages">
                {pages.map(page => <option key={page._id} value={page.slug}>{page.title}</option>)}
              </optgroup>
              <optgroup label="Blog Posts">
                {blogPosts.map(post => <option key={post._id} value={`blog/${post.slug}`}>{post.title}</option>)}
              </optgroup>
            </select>
          </div>

          {/* Analysis Results */}
          {selectedPageForAnalysis && pageAnalyses.find(a => a.url === `/${selectedPageForAnalysis}`) && (() => {
            const analysis = pageAnalyses.find(a => a.url === `/${selectedPageForAnalysis}`)!;
            return (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{analysis.title}</h3>
                      <p className="text-sm text-gray-500">{analysis.url}</p>
                    </div>
                    <ScoreCircle score={analysis.score} size="md" />
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Meta Title */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Tooltip text={SEO_TOOLTIPS.metaTitle}><span className="font-medium text-gray-900">Meta Title</span></Tooltip>
                      <StatusBadge status={analysis.meta.title.status} />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">"{analysis.meta.title.value || 'Not set'}"</p>
                    <p className="text-xs text-gray-500">{analysis.meta.title.length} characters • {analysis.meta.title.message}</p>
                  </div>

                  {/* Meta Description */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Tooltip text={SEO_TOOLTIPS.metaDescription}><span className="font-medium text-gray-900">Meta Description</span></Tooltip>
                      <StatusBadge status={analysis.meta.description.status} />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">"{analysis.meta.description.value || 'Not set'}"</p>
                    <p className="text-xs text-gray-500">{analysis.meta.description.length} characters • {analysis.meta.description.message}</p>
                  </div>

                  {/* H1 Tag */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Tooltip text={SEO_TOOLTIPS.h1Tag}><span className="font-medium text-gray-900">H1 Heading</span></Tooltip>
                      <StatusBadge status={analysis.headings.h1.status} />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{analysis.headings.h1.values[0] || 'Not found'}</p>
                    <p className="text-xs text-gray-500">{analysis.headings.h1.message}</p>
                  </div>

                  {/* Content */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Tooltip text={SEO_TOOLTIPS.wordCount}><span className="font-medium text-gray-900">Content</span></Tooltip>
                      <StatusBadge status={analysis.content.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{analysis.content.wordCount}</p>
                        <p className="text-xs text-gray-500">Words</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{analysis.content.readabilityScore}</p>
                        <Tooltip text={SEO_TOOLTIPS.readabilityScore}><p className="text-xs text-gray-500">Readability</p></Tooltip>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{analysis.content.readabilityGrade}</p>
                  </div>

                  {/* Images */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <Tooltip text={SEO_TOOLTIPS.altText}><span className="font-medium text-gray-900">Images</span></Tooltip>
                      <StatusBadge status={analysis.images.status} />
                    </div>
                    <p className="text-sm text-gray-600">{analysis.images.total} images found, {analysis.images.withAlt} with alt text</p>
                    <p className="text-xs text-gray-500 mt-1">{analysis.images.message}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* All Pages List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">All Pages SEO Status</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Page</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Score</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Title</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Description</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Content</th>
                  </tr>
                </thead>
                <tbody>
                  {pageAnalyses.map(analysis => (
                    <tr key={analysis.url} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedPageForAnalysis(analysis.url.slice(1))}>
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{analysis.title}</p>
                        <p className="text-xs text-gray-500">{analysis.url}</p>
                      </td>
                      <td className="py-3 px-4 text-center"><ScoreCircle score={analysis.score} size="sm" /></td>
                      <td className="py-3 px-4 text-center"><StatusBadge status={analysis.meta.title.status} /></td>
                      <td className="py-3 px-4 text-center"><StatusBadge status={analysis.meta.description.status} /></td>
                      <td className="py-3 px-4 text-center"><StatusBadge status={analysis.content.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Keywords Tab */}
      {activeTab === 'keywords' && (
        <div className="space-y-6">
          {/* Add Keyword Button */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-900">Tracked Keywords</h3>
              <p className="text-sm text-gray-500">Keywords you're optimizing your pages for</p>
            </div>
            <button onClick={() => setShowKeywordModal(true)}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2">
              <PlusIcon /> Add Keyword
            </button>
          </div>

          {/* Tracked Keywords */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trackedKeywords.map(kw => (
              <div key={kw.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">"{kw.keyword}"</span>
                      {kw.isPrimary && (
                        <Tooltip text={SEO_TOOLTIPS.primaryKeyword}>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Primary</span>
                        </Tooltip>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Target: {kw.targetPageTitle}</p>
                  </div>
                  <button onClick={() => handleRemoveKeyword(kw.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <TrashIcon />
                  </button>
                </div>
                
                {/* Keyword checks would go here - simplified for now */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Tooltip text="Is the keyword in your page title?"><span className="text-gray-600">In Title</span></Tooltip>
                    <span className="text-green-500">✓</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Tooltip text="Is the keyword in your H1 heading?"><span className="text-gray-600">In H1</span></Tooltip>
                    <span className="text-green-500">✓</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Tooltip text="Is the keyword in your page URL?"><span className="text-gray-600">In URL</span></Tooltip>
                    <span className="text-yellow-500">!</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Tooltip text={SEO_TOOLTIPS.keywordDensity}><span className="text-gray-600">Density</span></Tooltip>
                    <span className="text-gray-900">1.2%</span>
                  </div>
                </div>
              </div>
            ))}
            
            {trackedKeywords.length === 0 && (
              <div className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="font-semibold text-gray-900 mb-2">No keywords tracked yet</h3>
                <p className="text-gray-500 mb-4">Add keywords to track how well your pages are optimized for them.</p>
                <button onClick={() => setShowKeywordModal(true)} className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors">
                  Add Your First Keyword
                </button>
              </div>
            )}
          </div>

          {/* Suggested Keywords */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Suggested Keywords for Car Rental
              <Tooltip text="These are popular keywords in the car rental industry. Add ones relevant to your business.">
                <span></span>
              </Tooltip>
            </h3>
            <p className="text-sm text-gray-500 mb-4">Click to add any of these keywords to your tracking list</p>
            
            <div className="space-y-4">
              {['primary', 'long-tail', 'local', 'service', 'comparison'].map(category => (
                <div key={category}>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize flex items-center gap-2">
                    {category === 'primary' && <Tooltip text={SEO_TOOLTIPS.primaryKeyword}><span>{category}</span></Tooltip>}
                    {category === 'long-tail' && <Tooltip text={SEO_TOOLTIPS.longTailKeyword}><span>{category.replace('-', ' ')}</span></Tooltip>}
                    {!['primary', 'long-tail'].includes(category) && <span>{category}</span>}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {suggestedKeywords.filter(k => k.category === category).map(kw => (
                      <button key={kw.keyword} onClick={() => { setNewKeyword({ ...newKeyword, keyword: kw.keyword }); setShowKeywordModal(true); }}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-green-100 hover:text-green-700 transition-colors flex items-center gap-1">
                        {kw.keyword}
                        <span className="text-xs text-gray-400">+</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Content Quality Overview
              <Tooltip text="How well your content is optimized for both readers and search engines.">
                <span></span>
              </Tooltip>
            </h3>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Page</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                      <Tooltip text={SEO_TOOLTIPS.wordCount}><span>Words</span></Tooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                      <Tooltip text={SEO_TOOLTIPS.readabilityScore}><span>Readability</span></Tooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                      <Tooltip text={SEO_TOOLTIPS.h1Tag}><span>H1</span></Tooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">
                      <Tooltip text={SEO_TOOLTIPS.h2Tag}><span>H2s</span></Tooltip>
                    </th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pageAnalyses.map(analysis => (
                    <tr key={analysis.url} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{analysis.title}</p>
                        <p className="text-xs text-gray-500">{analysis.url}</p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-medium ${analysis.content.wordCount >= 800 ? 'text-green-600' : analysis.content.wordCount >= 300 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {analysis.content.wordCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div>
                          <span className={`font-medium ${analysis.content.readabilityScore >= 60 ? 'text-green-600' : analysis.content.readabilityScore >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {analysis.content.readabilityScore}
                          </span>
                          <p className="text-xs text-gray-500">{analysis.content.readabilityGrade.split('(')[0]}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={analysis.headings.h1.count === 1 ? 'text-green-600' : 'text-red-600'}>
                          {analysis.headings.h1.count === 1 ? '✓' : analysis.headings.h1.count === 0 ? '✗' : `${analysis.headings.h1.count}!`}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-600">{analysis.headings.h2.count}</td>
                      <td className="py-3 px-4 text-center"><StatusBadge status={analysis.content.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Content Tips */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">💡 Content Writing Tips</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Word Count Guidelines</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Homepage: 500-800 words</li>
                  <li>• Service pages: 800-1500 words</li>
                  <li>• Blog posts: 1000-2500 words</li>
                  <li>• Landing pages: 500-1000 words</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Readability Tips</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Use short sentences (under 20 words)</li>
                  <li>• Break up text with headings</li>
                  <li>• Use bullet points for lists</li>
                  <li>• Avoid jargon and complex words</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Tab */}
      {activeTab === 'technical' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sitemap */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🗺️ Sitemap
                  <Tooltip text={SEO_TOOLTIPS.sitemap}><span></span></Tooltip>
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Active</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">A sitemap helps Google discover all your pages.</p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm"><span className="text-gray-500">Pages indexed:</span> <span className="font-medium">{pageAnalyses.length}</span></p>
                <p className="text-sm"><span className="text-gray-500">Last updated:</span> <span className="font-medium">Today</span></p>
              </div>
              <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
                Regenerate Sitemap
              </button>
            </div>

            {/* Robots.txt */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🤖 Robots.txt
                  <Tooltip text={SEO_TOOLTIPS.robotsTxt}><span></span></Tooltip>
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Configured</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Controls which pages search engines can crawl.</p>
              <div className="bg-gray-900 rounded-lg p-4 mb-4 font-mono text-xs text-green-400">
                User-agent: *<br/>
                Allow: /<br/>
                Sitemap: /sitemap.xml
              </div>
              <button className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium">
                Edit Robots.txt
              </button>
            </div>

            {/* HTTPS */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🔒 HTTPS Security
                  <Tooltip text={SEO_TOOLTIPS.https}><span></span></Tooltip>
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Secure</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Your site uses HTTPS, which is required for good rankings.</p>
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-medium">SSL Certificate Active</span>
              </div>
            </div>

            {/* Mobile Friendly */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  📱 Mobile Friendly
                  <Tooltip text={SEO_TOOLTIPS.mobileFriendly}><span></span></Tooltip>
                </h3>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Responsive</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">Your site adapts to mobile devices, which Google requires.</p>
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Responsive Design Detected</span>
              </div>
            </div>
          </div>

          {/* Page Speed */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              ⚡ Page Speed Recommendations
              <Tooltip text={SEO_TOOLTIPS.pageSpeed}><span></span></Tooltip>
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-yellow-500">⚠️</span>
                  <span className="text-sm text-gray-700">Optimize images - some images could be compressed</span>
                </div>
                <span className="text-xs text-yellow-600 font-medium">Medium Impact</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-blue-500">💡</span>
                  <span className="text-sm text-gray-700">Enable lazy loading for images below the fold</span>
                </div>
                <span className="text-xs text-blue-600 font-medium">Suggestion</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-green-500">✓</span>
                  <span className="text-sm text-gray-700">JavaScript and CSS are minified</span>
                </div>
                <span className="text-xs text-green-600 font-medium">Good</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Keyword Modal */}
      {showKeywordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slideUp">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-5 text-white">
              <h2 className="text-xl font-bold">Add Keyword to Track</h2>
              <p className="text-green-100 text-sm">Track how well a page is optimized for a keyword</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Keyword
                  <Tooltip text="The word or phrase you want to rank for in Google."><span></span></Tooltip>
                </label>
                <input type="text" value={newKeyword.keyword} onChange={(e) => setNewKeyword({ ...newKeyword, keyword: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., car rental Perth" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Target Page
                  <Tooltip text="The page you want to rank for this keyword."><span></span></Tooltip>
                </label>
                <select value={newKeyword.pageId} onChange={(e) => setNewKeyword({ ...newKeyword, pageId: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 bg-white">
                  <option value="">Select a page...</option>
                  <optgroup label="Website Pages">
                    {pages.map(page => <option key={page._id} value={page._id}>{page.title}</option>)}
                  </optgroup>
                  <optgroup label="Blog Posts">
                    {blogPosts.map(post => <option key={post._id} value={post._id}>{post.title}</option>)}
                  </optgroup>
                </select>
              </div>
              
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer">
                <input type="checkbox" checked={newKeyword.isPrimary} onChange={(e) => setNewKeyword({ ...newKeyword, isPrimary: e.target.checked })}
                  className="w-5 h-5 rounded text-green-600 focus:ring-green-500" />
                <div>
                  <span className="text-sm font-medium text-gray-900">Primary Keyword</span>
                  <Tooltip text={SEO_TOOLTIPS.primaryKeyword}><span></span></Tooltip>
                  <p className="text-xs text-gray-500">Mark this as the main keyword for this page</p>
                </div>
              </label>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => { setShowKeywordModal(false); setNewKeyword({ keyword: '', pageId: '', isPrimary: false }); }}
                className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors">
                Cancel
              </button>
              <button onClick={handleAddKeyword} disabled={!newKeyword.keyword || !newKeyword.pageId}
                className="px-5 py-2.5 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50">
                Add Keyword
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEODashboard;
